import React, { useEffect, Suspense, lazy } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Provider } from "react-redux";
import { ThemeProvider } from "@mui/material/styles";
import {
  CssBaseline,
  Snackbar,
  Alert,
  CircularProgress,
  Box,
} from "@mui/material";
import { store } from "@/store";
import { theme } from "@/theme";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import { fetchAllData } from "@/store/slices/dataSlice";
import { hideSnackbar } from "@/store/slices/uiSlice";
import Shell from "@/components/templates/Shell";
import { APP_ROUTES } from "@/constants/routes";
import ErrorBoundary from "@/components/molecules/ErrorBoundary";
import { checkPermission } from "@/utils/permissions";


const Login = lazy(() => import("@/pages/Login/Login"));
const Dashboard = lazy(() => import("@/pages/Dashboard/Dashboard"));
const Chat = lazy(() => import("@/pages/Chat/Chat"));
const Employees = lazy(() => import("@/pages/Employees/Employees"));
const Attendance = lazy(() => import("@/pages/Attendance/Attendance"));
const Leave = lazy(() => import("@/pages/Leave/Leave"));
const Payroll = lazy(() => import("@/pages/Payroll/Payroll"));
const Recruitment = lazy(() => import("@/pages/Recruitment/Recruitment"));
const Onboarding = lazy(() => import("@/pages/Onboarding/Onboarding"));
const Performance = lazy(() => import("@/pages/Performance/Performance"));
const Training = lazy(() => import("@/pages/Training/Training"));
const Departments = lazy(() => import("@/pages/Departments/Departments"));
const Designations = lazy(() => import("@/pages/Designations/Designations"));
const Hierarchy = lazy(() => import("@/pages/Hierarchy/Hierarchy"));
const Reports = lazy(() =>
  import("@/pages/Reports/Reports").then((m) => ({ default: m.Reports || m.default })),
);
const Settings = lazy(() =>
  import("@/pages/Settings/Settings").then((m) => ({ default: m.Settings || m.default })),
);
const MyProfile = lazy(() =>
  import("@/pages/MyProfile/MyProfile").then((m) => ({ default: m.MyProfile || m.default })),
);
const TasksPage = lazy(() => import("@/pages/Tasks"));
const CustomReportBuilder = lazy(() =>
  import("@/pages/Customereportbuilder/customereportbuilder").then((m) => ({ default: m.CustomReportBuilder || m.default })),
);
const ProjectsPage = lazy(() => import("@/pages/Projects/Projects"));
const Roles = lazy(() => import("@/pages/Roles/Roles"));
const Timesheets = lazy(() => import("@/pages/Timesheets/Timesheets"));
const Branches = lazy(() => import("@/pages/Branches/Branches"));
const Companies = lazy(() => import("@/pages/Companies/Companies"));
const CompanyProfile = lazy(() => import("@/pages/Companies/CompanyProfile"));
const Clients = lazy(() => import("@/pages/Clients/Clients"));
const Invoices = lazy(() => import("@/pages/Invoices/Invoices"));
const CompanyDocuments = lazy(
  () => import("@/pages/CompanyDocuments/CompanyDocuments"),
);
const Tracking = lazy(() => import("@/pages/Tracking/Tracking"));
const Shifts = lazy(() => import("@/pages/Shifts/Shifts"));

const ForgotPassword = lazy(() => import("@/pages/Login/ForgotPassword"));
const ResetPassword = lazy(() => import("@/pages/Login/ResetPassword"));

const LoadingFallback = () => (
  <Box
    sx={{
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      height: "100vh",
      width: "100%",
    }}
  >
    <CircularProgress />
  </Box>
);

const PrivateRoute: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const isAuthenticated = useAppSelector((s) => s.auth.isAuthenticated);
  return isAuthenticated ? <>{children}</> : <Navigate to="/login" replace />;
};

/**
 * Permission-based route guard
 * Checks if user has permission to access a module
 */
const PermissionGuardedRoute: React.FC<{ 
  module: string;
  children: React.ReactNode;
}> = ({ module, children }) => {
  const user = useAppSelector((s) => s.auth.user);
  const roles = useAppSelector((s) => s.data.roles) || [];
  const employees = useAppSelector((s) => s.data.employees) || [];

  // Super Admin always has access
  const roleStr = user?.role ? (typeof user.role === 'string' ? user.role.toLowerCase() : (user.role as any).name?.toLowerCase() || '') : '';
  const isSuperAdmin = roleStr === 'super admin' || roleStr === 'admin' || roleStr === 'master admin';

  // If roles haven't loaded yet, render the component and let it handle the loading state
  if (!roles || roles.length === 0) {
    return <>{children}</>;
  }

  // Check if user has canView permission for the module
  const hasPermission = checkPermission(user, roles, module, 'canView', employees);

  if (isSuperAdmin || hasPermission) {
    return <>{children}</>;
  }

  // Redirect to a page that never requires permission (myprofile). Redirecting
  // to "/app" would bounce straight back here via the index route's
  // "Navigate to dashboard" — creating an infinite redirect loop whenever the
  // user also lacks dashboard permission. "myprofile" has no permission guard
  // (see the route below and Shell.tsx's ALWAYS_ALLOWED_PAGES), so it's always
  // safe to land on.
  return <Navigate to="/app/myprofile" replace />;
};

// Roles & Permissions controls system-wide access, so it must never be
// reachable by a Manager or Employee through a manually entered URL.
const AdminOnlyRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const user = useAppSelector((s) => s.auth.user);
  const roles = useAppSelector((s) => s.data.roles) || [];
  const rawRole = user?.role;
  const assignedRole = typeof rawRole === "object"
    ? (rawRole as any)?.name
    : roles.find((role: any) => role._id === rawRole || role.id === rawRole)?.name || rawRole;
  const normalizedRole = String(assignedRole || "").trim().toLowerCase();
  const isAdmin = ["admin", "super admin", "master admin"].includes(normalizedRole);

  return isAdmin ? <>{children}</> : <Navigate to="/app/myprofile" replace />;
};

const AppContent: React.FC = () => {
  const dispatch = useAppDispatch();
  const snackbar = useAppSelector((s) => s.ui.snackbar);
  const users = useAppSelector((s) => s.data.users);
  const isAuthenticated = useAppSelector((s) => s.auth.isAuthenticated);

  useEffect(() => {
    if (isAuthenticated && users.length === 0) {
      dispatch(fetchAllData());
    }
  }, [dispatch, users.length, isAuthenticated]);
  return (
    <>
      <BrowserRouter
        future={{ v7_startTransition: true, v7_relativeSplatPath: true }}
      >
        <Suspense fallback={<LoadingFallback />}>
          <Routes>
            <Route path={APP_ROUTES.LOGIN} element={<Login />} />
            <Route
              path={APP_ROUTES.FORGOT_PASSWORD}
              element={<ForgotPassword />}
            />
            <Route
              path={APP_ROUTES.RESET_PASSWORD}
              element={<ResetPassword />}
            />
            <Route
              path={APP_ROUTES.APP}
              element={
                <PrivateRoute>
                  <Shell />
                </PrivateRoute>
              }
            >
              <Route index element={<Navigate to="dashboard" replace />} />
              <Route path="dashboard" element={
                <PermissionGuardedRoute module="dashboard">
                  <Dashboard />
                </PermissionGuardedRoute>
              } />
              <Route path="chat" element={
                <PermissionGuardedRoute module="chat">
                  <Chat />
                </PermissionGuardedRoute>
              } />
              <Route path="employees" element={
                <PermissionGuardedRoute module="employees">
                  <Employees />
                </PermissionGuardedRoute>
              } />
              <Route path="attendance" element={
                <PermissionGuardedRoute module="attendance">
                  <Attendance />
                </PermissionGuardedRoute>
              } />
              <Route path="leave" element={
                <PermissionGuardedRoute module="leave">
                  <Leave />
                </PermissionGuardedRoute>
              } />
              <Route path="payroll" element={
                <PermissionGuardedRoute module="payroll">
                  <Payroll />
                </PermissionGuardedRoute>
              } />
              <Route path="recruitment" element={
                <PermissionGuardedRoute module="recruitment">
                  <Recruitment />
                </PermissionGuardedRoute>
              } />
              <Route path="onboarding" element={
                <PermissionGuardedRoute module="onboarding">
                  <Onboarding />
                </PermissionGuardedRoute>
              } />
              <Route path="performance" element={
                <PermissionGuardedRoute module="performance">
                  <Performance />
                </PermissionGuardedRoute>
              } />
              <Route path="training" element={
                <PermissionGuardedRoute module="training">
                  <Training />
                </PermissionGuardedRoute>
              } />
              <Route path="departments" element={
                <PermissionGuardedRoute module="departments">
                  <Departments />
                </PermissionGuardedRoute>
              } />
              <Route path="designations" element={
                <PermissionGuardedRoute module="designations">
                  <Designations />
                </PermissionGuardedRoute>
              } />
              <Route path="hierarchy" element={
                <PermissionGuardedRoute module="hierarchy">
                  <Hierarchy />
                </PermissionGuardedRoute>
              } />
              <Route path="reports" element={
                <PermissionGuardedRoute module="reports">
                  <Reports />
                </PermissionGuardedRoute>
              } />
              <Route path="settings" element={
                <PermissionGuardedRoute module="settings">
                  <Settings />
                </PermissionGuardedRoute>
              } />
              <Route path="myprofile" element={<MyProfile />} />
              <Route path="tasks" element={
                <PermissionGuardedRoute module="tasks">
                  <TasksPage />
                </PermissionGuardedRoute>
              } />
              <Route path="projects" element={
                <PermissionGuardedRoute module="projects">
                  <ProjectsPage />
                </PermissionGuardedRoute>
              } />
              <Route path="roles" element={<AdminOnlyRoute><Roles /></AdminOnlyRoute>} />
              <Route path="timesheets" element={
                <PermissionGuardedRoute module="timesheets">
                  <Timesheets />
                </PermissionGuardedRoute>
              } />
              <Route path="branches" element={<Branches />} />
              <Route path="companies" element={
                <PermissionGuardedRoute module="companies">
                  <Companies />
                </PermissionGuardedRoute>
              } />
              <Route path="companies/:id" element={
                <PermissionGuardedRoute module="companies">
                  <CompanyProfile />
                </PermissionGuardedRoute>
              } />
              <Route path="mycompany" element={<CompanyProfile />} />
              <Route path="clients" element={
                <PermissionGuardedRoute module="clients">
                  <Clients />
                </PermissionGuardedRoute>
              } />
              <Route path="invoices" element={
                <PermissionGuardedRoute module="invoices">
                  <Invoices />
                </PermissionGuardedRoute>
              } />
              <Route path="company-documents" element={
                <PermissionGuardedRoute module="company-documents">
                  <CompanyDocuments />
                </PermissionGuardedRoute>
              } />
              <Route path="tracking" element={
                <PermissionGuardedRoute module="tracking">
                  <Tracking />
                </PermissionGuardedRoute>
              } />
              <Route path="shifts" element={
                <PermissionGuardedRoute module="shifts">
                  <Shifts />
                </PermissionGuardedRoute>
              } />
              <Route path="reports/custom-builder" element={
                <PermissionGuardedRoute module="reports">
                  <CustomReportBuilder />
                </PermissionGuardedRoute>
              } />
            </Route>
            <Route
              path="/"
              element={<Navigate to={APP_ROUTES.DASHBOARD} replace />}
            />
            <Route
              path="*"
              element={<Navigate to={APP_ROUTES.DASHBOARD} replace />}
            />
          </Routes>
        </Suspense>
      </BrowserRouter>
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => dispatch(hideSnackbar())}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => dispatch(hideSnackbar())}
          sx={{ borderRadius: 2 }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

import { HelmetProvider } from "react-helmet-async";
import { SocketProvider } from "@/context/SocketContext";
import IncomingCall from "@/components/chat/IncomingCall";

const AppContentWrapped: React.FC = () => {
  return (
    <>
      <AppContent />
      <IncomingCall />
    </>
  );
};

const App: React.FC = () => (
  <HelmetProvider>
    <Provider store={store}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <ErrorBoundary>
          <SocketProvider>
            <AppContentWrapped />
          </SocketProvider>
        </ErrorBoundary>
      </ThemeProvider>
    </Provider>
  </HelmetProvider>
);

export default App;